import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { View } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import timeGridPlugin from '@fullcalendar/timegrid';
import clone from 'just-clone';
import compare from 'just-compare';
import moment from 'moment';
import { combineLatest, ReplaySubject } from 'rxjs';
import { takeWhile } from 'rxjs/operators';
import { IDirectReport, IServiceItem, ITask } from '../../lookups';
import { IStatusMessage, Process, ResponseStatus } from '../../shared';
import {
    Dialog,
    FilterService,
    SelectItem
} from '../../shared/efaact-primeng.module';
import { FullCalendarComponent } from '../../shared/components/fullcalendar/fullcalendar.component';
import { IShowErrors } from '../../shared/errors-sidenav';
import { UnsavedChangesService } from '../../shared/modal-dialog';
import { LoggingService } from '../../shared/services/logging/logging.service';
import { IUserColor, IUserInfo, UserRoleEnum } from '../../user-info';
import { LeaveRequest, LeaveRequestStatus } from '../store/leave-request.model';
import { PayrollItem } from '../../payroll/item';
import { CompanySetting } from '../../company-setting';
let lrc: LeaveRequestComponent;
@Component({
    selector: 'ef-leave-request',
    templateUrl: './leave-request.component.html',
    styleUrls: ['./leave-request.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class LeaveRequestComponent implements OnInit, OnDestroy {
    private TypeSubject = new ReplaySubject<number>(1);
    private LeaveRequestsSubject = new ReplaySubject<
        Array<Partial<LeaveRequest>>
    >(1);
    private _leaverequests: Array<Partial<LeaveRequest>>;
    @Input() public set leaverequests(value: Array<Partial<LeaveRequest>>) {
        if (!value) {
            return;
        }
        this._leaverequests = clone(value).map(l => ({
            ...l,
            selected: false
        }));
        this.LeaveRequestsSubject.next(this._leaverequests);
    }
    public get leaverequests(): Array<Partial<LeaveRequest>> {
        if (Array.isArray(this._leaverequests)) {
            switch (this.leaveType) {
                case 0:
                    return this._leaverequests;
                    break;
                default:
                    return this._leaverequests.filter(
                        l => l.RequestStatusID === this.leaveType
                    );
                    break;
            }
        }
        // return this._leaverequests;
    }
    private _tasks: ITask[];
    @Input() public set tasks(value: ITask[]) {
        this._tasks = clone(value);
        this.taskSelectItems = value.map(t => ({
            label: t.TaskLongName,
            value: t.TaskListID
        }));
    }
    public get tasks(): ITask[] {
        return this._tasks;
    }
    private _payrollitems: PayrollItem[];
    @Input() public set payrollItems(value: PayrollItem[]) {
        this._payrollitems = clone(value);
        this.payrollItemSelectItems = value.map(t => ({
            label: t.PayrollItem,
            value: t.PIListID
        }));
    }
    public get payrollItems(): PayrollItem[] {
        return this._payrollitems;
    }
    private _serviceitems: IServiceItem[];
    @Input() public set serviceItems(value: IServiceItem[]) {
        this._serviceitems = clone(value);
        this.serviceItemSelectItems = value.map(t => ({
            label: t.ServiceItem,
            value: t.SIListID
        }));
    }
    public get serviceItems(): IServiceItem[] {
        return this._serviceitems;
    }
    payrollItemEditable: boolean;
    serviceItemEditable: boolean;
    private _settings: CompanySetting[];
    @Input() public set companySettings(value: CompanySetting[]) {
        this._settings = clone(value);
        this.createAuthSetting = value.find(x => x.SettingType == "LRCreateAuth").SettingValue == 1 ? true : false;
        if (this.createAuthSetting == true && this.process.ProcessID !== 69) {
            this.payrollItemEditable = true;
            this.serviceItemEditable = true;
        } else {
            this.payrollItemEditable = false;
            this.serviceItemEditable = false;
        }
    }
    public get companySettings(): CompanySetting[] {
        return this._settings;
    }
    public get AccrualInfo() {
        return this.selectedLeave
            ? this.selectedLeave.LeaveAccrualInfo
            : this.userInfo
            ? this.userInfo.LeaveAccrualInfo
            : '';
    }
    public get LeaveUser() {
        const selectedEmployee = this.selectedLeave
            ? this.directReports?.find(
                  x => x.EmployeeListID === this.selectedLeave.EmployeeListID
              )
            : null;
        return this.selectedLeave
            ? selectedEmployee?.EmployeeName || this.userInfo?.EmployeeName || ''
            : this.userInfo
            ? this.userInfo.EmployeeName
            : '';
    }
    private _selectedError: IStatusMessage;
    @Input() public set selectedError(value: IStatusMessage) {
        this.log.debug({
            type: 'LeaveRequestComponent:selectedError:set',
            value
        });
        if (!value) {
            return;
        }
        if (this._selectedError && this._selectedError.MsgEntityID) {
            const oldRow = document.getElementById(
                `row-${this._selectedError.MsgEntityID.toString()}`
            );
            if (oldRow) {
                oldRow.classList.remove('highlight');
                oldRow.classList.remove('highlight-warning');
                oldRow.classList.remove('highlight-danger');
            }
        }
        this._selectedError = value;
        if (!(value && value.MsgEntityID)) {
            return;
        }
        const row = document.getElementById(
            `row-${value.MsgEntityID.toString()}`
        );
        this.log.verbose({
            type: 'LeaveRequestComponent:selectedError',
            value,
            row
        });
        if (!row) {
            return;
        }
        row.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
        });
        row.classList.add('highlight');
        if (value.StatusCode === ResponseStatus.Warning) {
            row.classList.add('highlight-warning');
        }
        if (value.StatusCode === ResponseStatus.Failed) {
            row.classList.add('highlight-danger');
        }
    }
    public get selectedError(): IStatusMessage {
        return this._selectedError;
    }
    public get leaveSelectedCount(): number {
        return (
            this.leaverequests &&
            this.leaverequests.filter(x => x.selected).length
        );
    }
    public taskSelectItems: SelectItem[];
    @Input() userInfo: Partial<IUserInfo>;
    public directReportsSelectItems: SelectItem[];
    private DirectReportsSubject = new ReplaySubject<IDirectReport[]>(1);
    private _directReports: IDirectReport[];
    protected dateRange: Date[];
    @Input() public set directReports(value: IDirectReport[]) {
        this._directReports = value;
        this.DirectReportsSubject.next(this._directReports);
        this.directReportsSelectItems = value.map(d => ({
            label: d.EmployeeName,
            value: d.EmployeeListID
        }));
    }
    public get directReports(): IDirectReport[] {
        return this._directReports;
    }
    ProcessSubject = new ReplaySubject<Process>(1);
    @Input() public set process(value: Process) {
        this._process = value;
        if (!!value) {
            this.ProcessSubject.next(value);
        }
    }
    private WebColorsSubject = new ReplaySubject<Array<IUserColor>>(1);
    private _userWebColors: Array<IUserColor>;
    @Input() public set userWebColors(value: Array<IUserColor>) {
        this.log.verbose({
            type: 'LeaveRequestFeatureComponent:userWebColors:set',
            value
        });
        this._userWebColors = value;
        this.WebColorsSubject.next(this._userWebColors);
    }
    public get userWebColors(): Array<IUserColor> {
        return this._userWebColors;
    }
    @ViewChild('calendar', { static: true }) fc: FullCalendarComponent;

    private _process: Process;
    public get process(): Process {
        return this._process;
    }
    @Input() RequestStatusSelectItems: SelectItem[];
    createAuthSetting: boolean;
    payrollItemSelectItems: SelectItem[];
    serviceItemSelectItems: SelectItem[];
    events: Array<Partial<EventModel>>;
    options: any;
    urlDemo: string;
    urlTip: string;
    UrlDocumentation: string;
    showDisapprovePopup = false;
    disapproveComment: string;
    showPopup = false;
    newRequestForm: UntypedFormGroup;
    leaveTable: UntypedFormGroup;
    moment: any;
    dateFilters: any;
    dates: Date[] = [];
    public loading = true;
    public leaveType = 0;
    protected selectedLeave: LeaveRequest;
    selectedPayrollItem: string;
    private active = true;
    public UserRoleEnum = UserRoleEnum;
    @Output() public ShowError = new ReplaySubject<IShowErrors>(1);
    @Output() public Save = new ReplaySubject<Partial<LeaveRequest>>(1);
    @Output() public Approve = new ReplaySubject<Array<Partial<LeaveRequest>>>(
        1
    );
    @Output() public Unapprove = new ReplaySubject<
        Array<Partial<LeaveRequest>>
    >(1);
    @Output() public Disapprove = new ReplaySubject<
        Array<Partial<LeaveRequest>>
    >(1);
    @Output() public Delete = new ReplaySubject<Partial<LeaveRequest>>(1);
    @Output() public SetRole = new ReplaySubject<number>(1);
    @Output() public SetDateRange = new ReplaySubject<{
        startDate: Date;
        endDate: Date;
    }>(1);
    @ViewChild('leaveRequestDialog', { static: true })
    leaveRequestsDialog: Dialog;
    showAllButton: HTMLElement;
    showAll = false;
    private nrfc: {
        [key: string]: AbstractControl;
    };
    private _startDate: Date;
    private _endDate: Date;
    editing: LeaveRequest;
    cloned: { [i: number]: LeaveRequest } = {};
    constructor(
        private log: LoggingService,
        public confirmservice: UnsavedChangesService,
        private filterService: FilterService,
        protected cdRef: ChangeDetectorRef
    ) {
        this.newRequestForm = new UntypedFormGroup({
            task: new UntypedFormControl(),
            RequestHours: new UntypedFormControl(),
            AuthDesc: new UntypedFormControl(),
            EmpAuthStartDate: new UntypedFormControl(),
            EmpAuthEndDate: new UntypedFormControl(),
            AuthRequestID: new UntypedFormControl()
        });
        this.leaveTable = new UntypedFormGroup({
            task: new UntypedFormControl(),
            RequestHours: new UntypedFormControl(),
            AuthDesc: new UntypedFormControl(),
            EmpAuthStartDate: new UntypedFormControl(),
            EmpAuthEndDate: new UntypedFormControl(),
            AuthRequestID: new UntypedFormControl()
        });
        this.nrfc = this.newRequestForm.controls;
        lrc = this;
        this.options = {
            plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
            defaultDate: moment().format('YYYY-MM-DD'),
            height: 500,
            customButtons: {
                showAll: {
                    text: 'Show All',
                    click: function () {
                        lrc.showAllButton = this as HTMLElement;
                        lrc.showAll = !lrc.showAll;
                        if (lrc.showAll) {
                            lrc.SetDateRange.next({
                                startDate: null,
                                endDate: null
                            });
                            lrc.showAllButton.classList.remove(
                                'p-button-primary'
                            );
                            lrc.showAllButton.classList.add(
                                'p-button-primary'
                            );
                            lrc.showAllButton.textContent = 'Show Month';
                        } else {
                            lrc.SetDateRange.next({
                                startDate: lrc._startDate,
                                endDate: lrc._endDate
                            });
                            lrc.showAllButton.classList.remove(
                                'p-button-primary'
                            );
                            lrc.showAllButton.classList.add(
                                'p-button-primary'
                            );
                            lrc.showAllButton.textContent = 'Show All';
                        }
                        this.log.verbose({
                            type: 'LeaveRequestComponent:custombutton:click',
                            button: this
                        });
                    }
                }
            },
            header: {
                left: '',
                center: 'prev,title,next',
                right: 'showAll'
            },
            // validRange: {
            //     start: moment().format('YYYY-MM-DD'),
            //     end: moment().add(90, 'd').format('YYYY-MM-DD')
            // },
            selectable: true,
            select: this.datesSelected.bind(this),
            eventClick: this.eventSelected.bind(this),
            datesRender: this.datesRender.bind(this),
            eventRender: this.eventRender.bind(this)
        };
        this.moment = moment;
        this.setupSubscriptions();
        // this.TypeSubject.next(0);
    }
    eventRender(info: { event: EventModel; el: any; view: View }) {
        info.el.setAttribute('data-toggle', 'tooltip');
        info.el.setAttribute('data-placement', 'top');
        info.el.setAttribute('title', info.event.title);
    }
    datesRender(info) {
        this.log.verbose({
            type: 'LeaveRequestComponent.fullCalendar.datesRender',
            info
        });
        // this._startDate = moment(info.view.currentStart)
        this._startDate = moment(info.view.activeStart).toDate();
        // this._endDate = moment(info.view.currentEnd)
        this._endDate = moment(info.view.activeEnd).add(-1, 'seconds').toDate();
        this.log.verbose({
            type: 'LeaveRequestComponent.fullCalendar.datesRender',
            startDate: this._startDate,
            endDate: this._endDate
        });
        if (!this.showAll) {
            this.SetDateRange.next({
                startDate: this.showAll ? null : this._startDate,
                endDate: this.showAll ? null : this._endDate
            });
        }
    }
    datesSelected(selectInfo: SelectInfo) {
        this.log.verbose({
            type: 'LeaveRequestComponent.select',
            selectInfo
        });
        this.nrfc['EmpAuthStartDate'].setValue(selectInfo.start);
        this.nrfc['EmpAuthEndDate'].setValue(
            moment(selectInfo.end).add(-1, 'seconds').toDate()
        );
        this.dates = [
            selectInfo.start,
            moment(selectInfo.end).add(-1, 'seconds').toDate()
        ];
        this.nrfc['AuthRequestID'].setValue(-1);
        this.nrfc['task'].setValue(this.tasks[0]);
        this.showPopup = true;
        this.cdRef.detectChanges();
    }
    eventSelected(click: EventClickInfo) {
        this.log.verbose({
            type: 'LeaveRequestComponent.eventClick',
            event: click.event
        });
        const selectedRequest = this.leaverequests.find(
            x => x.AuthRequestID === Number(click.event.id)
        );
        if (selectedRequest && !this.isLeaveEditable(selectedRequest as LeaveRequest)) {
            this.showPopup = false;
            this.cdRef.detectChanges();
            return;
        }
        // if (click.event.extendedProps.IsReadOnly) {
        //     return;
        // }
        this.nrfc['AuthDesc'].setValue(click.event.title);
        this.nrfc['EmpAuthStartDate'].setValue(click.event.start);
        this.nrfc['EmpAuthEndDate'].setValue(
            moment(click.event.end).add(-1, 'seconds').toDate()
        );
        this.dates = [
            moment(click.event.start).toDate(),
            moment(click.event.end).add(-1, 'seconds').toDate()
        ];
        this.nrfc['AuthRequestID'].setValue(click.event.id);
        this.nrfc['RequestHours'].setValue(
            click.event.extendedProps.RequestHours
        );
        this.nrfc['task'].setValue(
            this.tasks.find(
                x => x.TaskListID === click.event.extendedProps.TaskListID
            )
        );
        if (this.leaveType != 2) {
            this.showPopup = true;
        } else {
            this.showPopup = false;
        }

        this.cdRef.detectChanges();
    }
    setupSubscriptions() {
        this.TypeSubject.pipe(takeWhile(() => this.active)).subscribe(
            t => (this.leaveType = t)
        );
        this.ProcessSubject.pipe(takeWhile(() => this.active)).subscribe(
            process => {
                if (process.ProcessID !== 69) {
                    this.setType(1);
                } else {
                    this.setType(0);
                }
                if (process) {
                    this.urlDemo = process.UrlDemo;
                    this.urlTip = process.UrlTip;
                    this.UrlDocumentation = process.UrlDocumentation;
                }
            }
        );
        combineLatest([
            this.LeaveRequestsSubject,
            this.DirectReportsSubject,
            this.WebColorsSubject,
            this.TypeSubject
        ])
            .pipe(takeWhile(() => this.active))
            .subscribe(([requests, reports, colors, type]) => {
                this.events = this.mapDataToEvent(
                    requests,
                    reports,
                    colors,
                    type
                );
                this.log.verbose({
                    type: 'LeaveRequestComponent:latest',
                    requests,
                    reports,
                    colors,
                    leavetype: type,
                    events: this.events
                });
                this.cdRef.detectChanges();
            });
        this.SetRole.pipe(takeWhile(() => this.active)).subscribe(() => {
            this.loading = true;
            this.cdRef.detectChanges();
        });
    }
    ngOnInit() {
        this.addDateRangeFilter();
    }
    ngOnDestroy() {
        this.active = false;
    }
    mapDataToEvent(
        data: Array<Partial<LeaveRequest>>,
        directReports: IDirectReport[],
        colors: IUserColor[],
        type: number
    ): Array<Partial<EventModel>> {
        this.cdRef.detectChanges();
        let filtered;
        this.log.verbose({
            name: 'LeaveRequestComponent:mapDataToEvent',
            data,
            directReports,
            colors,
            type
        });
        switch (type) {
            case 0:
                filtered = data;
                break;
            default:
                filtered = data.filter(
                    l => l.RequestStatusID === this.leaveType
                );
                break;
        }
        const mapped = filtered.map(l => {
            const color = colors.find(x => x.StatusID === l.RequestStatusID);
            const report = directReports.find(
                x => x.EmployeeListID === l.EmployeeListID
            );
            const event = {
                id: l.AuthRequestID ? l.AuthRequestID.toString() : -1,
                allDay: true,
                start: l.EmpAuthStartDate,
                end: moment(l.EmpAuthEndDate).add(1, 'second').toDate(),
                title: `${
                    type !== 0
                        ? `${report?.EmployeeName || ''}${
                              report ? ': ' : ''
                          }`
                        : ''
                }${l.AuthDesc || '(No Description)'}`,
                backgroundColor: (color && color.Backcolor_hex) || '#0000CC',
                textColor: (color && color.Forecolor_Hex) || '#000000',
                extendedProps: {
                    RequestHours: l.RequestHours,
                    TaskListID: l.TaskListID
                    // IsReadOnly:
                    //     !(
                    //         l.RequestStatusID === LeaveRequestStatus.Saved ||
                    //         l.RequestStatusID === LeaveRequestStatus.Signed ||
                    //         l.RequestStatusID === LeaveRequestStatus.Disapproved
                    //     ) || this.process.ProcessID !== 69
                }
            } as EventModel;
            return event;
        });
        this.log.verbose({
            type: 'LeaveRequestComponent:mapData',
            data,
            mapped
        });
        this.loading = false;
        return mapped;
    }
    onDelete(event: LeaveRequest) {
        this.log.verbose({
            type: 'LeaveRequestComponent:onDelete',
            event
        });
        this.confirmservice
            .confirmPromise({
                dialogText: `Are you sure you want to delete ${event.AuthDesc}?`
            })
            .then(() => {
                this.Delete.next(event);
                return;
            })
            .catch(e => {});
    }
    onRowEditInit(item: LeaveRequest) {
        if (!this.isLeaveEditable(item)) {
            return;
        }
        this.editing = { ...item };
        this.cloned[item.AuthRequestID] = { ...item };
        this.payrollItemSelectItems = this.payrollItems.filter(x => x.EmployeeListID == item.EmployeeListID).map(t => ({
            label: t.PayrollItem,
            value: t.PIListID
        }));
        this.selectedPayrollItem = item.PIListID;
        this.log.verbose({
            type: 'LeaveRequestComponent:onEditInit',
            $event: item,
            editing: this.editing
        });
    }
    onRowEditSave(item: LeaveRequest) {
        if (!this.isLeaveEditable(item)) {
            this.onRowEditCancel(item);
            return;
        }
        this.log.verbose({
            type: 'LeaveRequestComponent:onEditComplete',
            $event: item,
            editing: this.editing
        });
        // this.events = this.mapData(this.leaverequests);
        // this.cdRef.detectChanges();
        this.onSave({
            ...item,
            EmpAuthStartDate: item.dates[0],
            EmpAuthEndDate: item.dates[1],
            task: { TaskListID: item.TaskListID },
            PIListID: this.selectedPayrollItem
        });
        delete this.cloned[item.AuthRequestID];
        delete this.editing;
    }
    onRowEditCancel(item: LeaveRequest) {
        this.log.verbose({
            type: 'LeaveRequestComponent:onEditCancel',
            item
        });
        this.selectedPayrollItem = item.PIListID;
        const index = this.leaverequests.findIndex(
            x => x.AuthRequestID === item.AuthRequestID
        );
        if (index >= 0) {
            this.leaverequests.splice(
                index,
                1,
                this.cloned[item.AuthRequestID]
            );
        }
        delete this.editing;
        delete this.cloned[item.AuthRequestID];
    }
    onTaskChanged(item) {
        this.selectedPayrollItem = 'SYSPI0000000000000000000000000000000';
        item = {
            ...item,
            PIListID: this.selectedPayrollItem
        };
    }
    onPayrollItemChanged($event) {
        this.selectedPayrollItem = $event.value;
    }
    onSave($event) {
        this.log.verbose({
            type: 'LeaveRequestComponent:onSave',
            $event,
            dates: this.dates
        });
        const fc = this.newRequestForm.controls;

        const existingRequest =
            Number($event.AuthRequestID) > 0
                ? this.leaverequests.find(
                      x => x.AuthRequestID === Number($event.AuthRequestID)
                  )
                : null;
        if (existingRequest && !this.isLeaveEditable(existingRequest as LeaveRequest)) {
            this.showPopup = false;
            this.clearLeaveRequest();
            this.cdRef.detectChanges();
            return;
        }

        const leave = {
            PIListID: this.selectedPayrollItem || $event.PIListID,
            SIListID: $event.SIListID || null,
            EmployeeListID: this.userInfo.EmployeeListID,
            AuthRequestID: Number($event.AuthRequestID),
            EmpAuthStartDate:
                this.dates && this.dates.length === 2
                    ? moment(this.dates[0]).toDate()
                    : $event.EmpAuthStartDate,
            EmpAuthEndDate:
                this.dates && this.dates.length === 2
                    ? moment(this.dates[1]).toDate()
                    : $event.EmpAuthEndDate,
            // EmpAuthStartDate: (!!$event.EmpAuthStartDate) ? $event.EmpAuthStartDate : moment(this.dates[0]).toDate(),
            // EmpAuthEndDate: (!!$event.EmpAuthEndDate) ? $event.EmpAuthEndDate : moment(this.dates[1]).toDate(),
            AuthDesc: $event.AuthDesc,
            RequestStatus: 'pending',
            TaskListID: $event.task.TaskListID,
            RequestStatusID: LeaveRequestStatus.Signed,
            RequestHours: Number($event.RequestHours),
            Status: {
                StatusCode: ResponseStatus.Loading,
                Message: 'Saving'
            }
        };
        this.dates = [];
        this.log.verbose({
            type: 'LeaveRequestComponent:onSave',
            leave,
            leaverequests: this.leaverequests
        });

        // this.leaverequests = [
        //     ...this.leaverequests.filter(
        //         x => x.AuthRequestID !== leave.AuthRequestID
        //     ),
        //     leave
        // ];
        this.Save.next(leave);
        this.showPopup = false;
        this.clearLeaveRequest();
        this.cdRef.detectChanges();
    }
    Cancel() {
        this.showPopup = false;
        this.clearLeaveRequest();
        this.cdRef.detectChanges();
    }
    clearLeaveRequest() {
        this.newRequestForm.reset();
    }
    onRowSelect($event) {
        const event: LeaveRequest = $event.data;
        this.selectedLeave = event;
        this.log.verbose({
            type: 'LeaveRequestComponent:onRowSelect',
            $event,
            event
        });
        this.fc.gotoDate(moment(event.EmpAuthStartDate).toDate());
        this.cdRef.detectChanges();
    }
    addDateRangeFilter() {
        this.filterService.register(
            'dateRangeFilter',
            (value, filter): boolean => {
                this.log.verbose({
                    type: 'LeaveRequestComponent:dateRangeFilter',
                    value,
                    filter,
                    dateFilters: this.dateFilters
                });
                if (value === undefined || value === null) {
                    return false;
                }
                return moment(value).isBetween(
                    moment(this.dateFilters[0]).add(-1, 'second'),
                    moment(this.dateFilters[1]).add(1, 'second')
                );
                // get the from/start value
            }
        );
    }
    setType(t, e = null) {
        this.log.verbose({ type: 'LeaveRequestComponent:setType', t });
        const oldType = this.leaveType;
        this.leaveType = t;
        switch (t) {
            case 0:
                if (oldType !== 0) {
                    this.SetRole.next(null);
                }
                break;
            case 1:
                if (oldType === 0) {
                    this.SetRole.next(UserRoleEnum.Supervisor);
                }
                break;
            case 2:
                if (oldType === 0) {
                    this.SetRole.next(UserRoleEnum.Supervisor);
                }
                break;
            case 3:
                if (oldType === 0) {
                    this.SetRole.next(UserRoleEnum.Supervisor);
                }
                break;
            default:
                if (oldType !== 0) {
                }
                this.SetRole.next(null);
                break;
        }
        this.TypeSubject.next(t);
    }
    approveRequests() {
        this.log.verbose({
            type: 'LeaveRequestComponent:approveRequests'
        });
        this.Approve.next(
            this.leaverequests
                .filter(x => x.selected)
                .map(x => ({
                    ...x,
                    RequestStatusID: LeaveRequestStatus.Approved
                }))
        );
    }
    unApproveRequests() {
        this.log.verbose({
            type: 'LeaveRequestComponent:unApproveRequests'
        });
        this.Unapprove.next(
            this.leaverequests
                .filter(x => x.selected)
                .map(x => ({
                    ...x,
                    RequestStatusID: LeaveRequestStatus.Signed
                }))
        );
    }
    disApproveRequests() {
        this.log.verbose({
            type: 'LeaveRequestComponent:disApproveRequests'
        });
        this.Disapprove.next(
            this.leaverequests
                .filter(x => x.selected)
                .map(x => ({
                    ...x,
                    RequestStatusID: LeaveRequestStatus.Disapproved,
                    AuthDesc: this.disapproveComment != null ? this.disapproveComment : x.AuthDesc
                }))
        );
        this.showDisapprovePopup = false;
        this.disapproveComment = null;
    }
    cancelDisapprove() {
        this.showDisapprovePopup = false;
        this.disapproveComment = null;
    }
    onStatusClick(item: LeaveRequest) {
        this.ShowError.next({
            open: true,
            MsgEntityID: item.AuthRequestID.toString()
        });
    }
    isLeaveEditable(item: Partial<LeaveRequest>): boolean {
        if (!item) {
            return false;
        }
        return item.RequestStatusID !== LeaveRequestStatus.Approved;
    }
}

interface SelectInfo {
    // Date. A date indicating the beginning of the selection.
    start: Date;

    // Date. A date indicating the end of the selection.
    // In line with the discussion about the Event object, it is important to stress that the end date property is exclusive. For example, if the selection is all-day and the last day is a Thursday, end will be Friday.
    end: Date;
    // String. An ISO8601 string representation of the start date. It will have a timezone offset similar to the calendar’s timeZone e.g. 2018-09-01T12:30:00-05:00. If selecting all-day cells, it won’t have a time nor timezone part e.g. 2018-09-01.
    startStr: string;

    // String. An ISO8601 string representation of the end date. It will have a timezone offset similar to the calendar’s timeZone e.g. 2018-09-02T12:30:00-05:00. If selecting all-day cells, it won’t have a time nor timezone part e.g. 2018-09-02.
    endStr: string;
    // Boolean. true or false whether the selection happened on all-day cells.
    allDay: boolean;

    // The native JavaScript event with low-level information such as click coordinates.
    jsEvent: any;

    // View object. The current Calendar view.
    view: any;

    // Resource object. If the current view is a resource view, this is the Resource object that was selected. This is only available when using one of the resource plugins.
    resource: any;
}
interface EventClickInfo {
    // The associated Event Object.
    event: EventModel;

    // The HTML element for this event.
    el: HTMLElement;

    // The native JavaScript event with low-level information such as click coordinates.
    jsEvent: any;

    view: any;
}
interface EventModel {
    id: string;
    groupId: string;
    allDay: boolean;
    start: Date;
    end: Date;
    title: string;
    url: string;
    classNames: string[];
    editable: boolean;
    startEditable: boolean;
    durationEditable: boolean;
    resourceEditable: boolean;
    rendering: string;
    overlap: boolean;
    constraint: any;
    backgroundColor: string;
    borderColor: string;
    textColor: string;
    extendedProps: any;
    source: any;
    data: any;
}
